import {Stack, ToggleButton, ToggleButtonGroup} from '@mui/material'
import type {ReactElement} from 'react'
import {useTranslation} from 'react-i18next'
import WholeNumberField from '@shared/components/base/inputs/WholeNumberField'
import {
  MAX_ROLLING_RANGE_COUNT,
  ROLLING_RANGE_UNITS,
  type RollingRangeUnit,
} from '@shared/utils/timeRange'

interface Props {
  count: number | null
  unit: RollingRangeUnit
  onCountChange: (count: number | null) => void
  onUnitChange: (unit: RollingRangeUnit) => void
  /** Prefix of the fields' data-cy anchors, e.g. "dashboard" or "widget". */
  dataCyPrefix: string
  autoFocus?: boolean
}

/**
 * "Last N hours / days" with a free N (AB#6936): the number and its unit. The caller
 * decides when to apply it; an empty or too-large number is shown as an error here
 * and must not be applied.
 */
const RollingRangeFields = ({
  count,
  unit,
  onCountChange,
  onUnitChange,
  dataCyPrefix,
  autoFocus = false,
}: Props): ReactElement => {
  const {t} = useTranslation('timerange')
  const max = MAX_ROLLING_RANGE_COUNT[unit]
  const tooLarge = count !== null && count > max
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <WholeNumberField
        size="small"
        label={t('rolling_count')}
        value={count}
        onChange={onCountChange}
        min={1}
        emptyError={t('rolling_count_required')}
        helperText={tooLarge ? t('rolling_count_max', {max}) : undefined}
        FormHelperTextProps={{sx: {color: tooLarge ? 'error.main' : undefined}}}
        autoFocus={autoFocus}
        sx={{width: 120}}
        inputProps={{'data-cy': `${dataCyPrefix}-rolling-range-count`, max}}
        // Typing a digit must not jump to a menu item starting with it.
        onKeyDown={(event) => event.stopPropagation()}
      />
      <ToggleButtonGroup
        size="small"
        exclusive
        value={unit}
        onChange={(_, next: RollingRangeUnit | null) => next && onUnitChange(next)}
        aria-label={t('rolling_unit')}
        sx={{height: 40}}
      >
        {ROLLING_RANGE_UNITS.map((option) => (
          <ToggleButton
            key={option}
            value={option}
            data-cy={`${dataCyPrefix}-rolling-range-unit-${option}`}
          >
            {t(`rolling_unit_${option}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  )
}

export default RollingRangeFields
